import { type ReactNode } from 'react';
import { Icon } from './Icon';
import { IconButton, ProgressBar } from './primitives';

function fmt(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(Math.floor(s % 60)).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export interface AudioPlayerProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  duration?: number;
  position?: number;
  playing?: boolean;
  onToggle?: () => void;
  /**
   * When provided, the progress bar becomes click-to-seek. Purely a callback
   * like onToggle — the shell stays free of media logic; whoever owns the
   * <audio> element (components/AudioPlayback) does the seeking.
   */
  onSeek?: (seconds: number) => void;
  variant?: 'default' | 'brand';
}

export function AudioPlayer({
  title,
  subtitle,
  duration = 1800,
  position = 0,
  playing = false,
  onToggle,
  onSeek,
  variant = 'default',
}: AudioPlayerProps) {
  const brand = variant === 'brand';
  return (
    <div
      className="zen-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-5)',
        padding: 'var(--space-5)',
        background: brand ? 'var(--navy-600)' : 'var(--surface-card)',
        borderColor: brand ? 'transparent' : undefined,
      }}
    >
      <IconButton
        icon={playing ? 'pause' : 'play'}
        label={playing ? 'Pause' : 'Play'}
        variant={brand ? 'outline' : 'solid'}
        size="lg"
        onClick={onToggle}
        style={brand ? { background: 'var(--white)', color: 'var(--navy-700)', borderColor: 'transparent' } : undefined}
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-4)' }}>
          <span
            style={{
              font: 'var(--type-h4)',
              color: brand ? 'var(--white)' : 'var(--text-strong)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </span>
          {subtitle && (
            <span style={{ font: 'var(--type-caption)', color: brand ? 'var(--navy-200)' : 'var(--text-muted)' }}>{subtitle}</span>
          )}
        </div>
        <div
          style={onSeek ? { cursor: 'pointer' } : undefined}
          onClick={
            onSeek
              ? (e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                  onSeek(ratio * duration);
                }
              : undefined
          }
        >
          <ProgressBar
            value={position}
            max={duration}
            color={brand ? 'var(--white)' : undefined}
            style={brand ? { background: 'rgba(255,255,255,.25)' } : undefined}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            font: 'var(--type-caption)',
            color: brand ? 'var(--navy-200)' : 'var(--text-muted)',
          }}
        >
          <span>{fmt(position)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
      <Icon name="headphones" size="18px" style={{ color: brand ? 'var(--navy-200)' : 'var(--navy-300)' }} />
    </div>
  );
}
